import {
  BackpackThemeProvider,
  Box,
  Grid,
  Icon,
  ProgressBar,
  Tab,
  TabContext,
  TabList,
  TabPanel,
  Theme,
  Typography
} from "@outschool/backpack";
import {
  ClassroomPostFilter,
  LearningStatementsVerb
} from "@outschool/gql-frontend-generated";
import {
  faCalendar,
  faCalendarXmark,
  faCheckCircle,
  faChevronDownRegular,
  faChevronRightRegular,
  faDiamondExclamation,
  faLockRegular
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { LearnerRoutes } from "@outschool/routes";
import * as Time from "@outschool/time";
import { LegacyThemeProvider } from "@outschool/ui-legacy-component-library";
import { Redirect, useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";

import useAnalyticsPage, {
  PageType
} from "../../lib/analytics/useAnalyticsPage";
import {
  ClassroomPostsProvider,
  useClassroomPostsContext
} from "../../providers/ClassroomPostsProvider";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import {
  Lesson,
  SectionLesson,
  useLearnerClassroomContext
} from "../../providers/LearnerClassroomProvider";
import LearnerClassPost from "./ClassPost";
import {
  AllLessonsCompletedView,
  LessonViewHeading,
  SectionLessonView
} from "./lessonsPage/LessonView";

const ALL_LESSONS_COMPLETE_TAB_PANEL_KEY = "lessons-complete";

interface ClassroomLessonsPageProps {
  lessonUid?: string;
}

export const ClassroomLessonsPage = ({
  lessonUid
}: ClassroomLessonsPageProps) => {
  const { t } = useTranslation(
    "learnerApp\\LearnerClassroom\\ClassroomLessonsPage"
  );
  const {
    sectionUid,
    section,
    syllabus,
    hasLessons,
    allLessonsCompleted,
    sectionLessons
  } = useLearnerClassroomContext();
  useAnalyticsPage({ pageName: PageType.Lessons, pageParams: { sectionUid } });

  if (!hasLessons) {
    return <Redirect to={LearnerRoutes.classroomPath(sectionUid)} />;
  }

  const lessons: Lesson[] = syllabus?.lessons || [];

  const isFirstLesson =
    lessonUid === lessons[0]?.uid || lessonUid === sectionLessons?.[0]?.uid;

  if (!lessonUid) {
    if (allLessonsCompleted) {
      lessonUid = ALL_LESSONS_COMPLETE_TAB_PANEL_KEY;
    } else {
      const currentLesson =
        section?.currentSectionLesson || syllabus?.currentLesson;
      const lesson = currentLesson || sectionLessons?.[0] || lessons[0];
      return (
        <Redirect
          to={
            lesson
              ? LearnerRoutes.lessonsPath(sectionUid, lesson.uid)
              : LearnerRoutes.classroomPath(sectionUid)
          }
        />
      );
    }
  }

  // LegacyThemeProvider injects breakpoints which completely breaks how Grid works in MUI
  // Make BackpackThemeProvider wrap it immediately to avoid even referencing the LegacyThemeProvider
  return (
    <BackpackThemeProvider>
      <TabContext value={lessonUid}>
        <Grid container spacing={48} sx={{ flex: 1, paddingTop: 36 }}>
          <Grid xs={12} md={4}>
            <LessonsNavigationForSectionLessons selectedLessonUid={lessonUid} />
          </Grid>
          <Grid
            xs={12}
            md={8}
            sx={(theme: Theme) => ({
              [theme.breakpoints.down("md")]: {
                marginX: 16
              }
            })}
          >
            <LegacyThemeProvider>
              {sectionLessons?.map(lessonOrSectionLesson => (
                <TabPanel
                  key={lessonOrSectionLesson.uid}
                  value={lessonOrSectionLesson.uid}
                >
                  {section?.is_self_paced && isFirstLesson && (
                    <ClassroomPostsProvider
                      postsFilter={ClassroomPostFilter.Announcements}
                    >
                      <IntegratedWelcomePosts />
                    </ClassroomPostsProvider>
                  )}
                  <SelectedSectionLessonView
                    sectionLesson={
                      lessonOrSectionLesson as unknown as SectionLesson
                    }
                  />
                </TabPanel>
              ))}
              <TabPanel
                key={ALL_LESSONS_COMPLETE_TAB_PANEL_KEY}
                value={ALL_LESSONS_COMPLETE_TAB_PANEL_KEY}
              >
                <AllLessonsCompletedView
                  heading={
                    <LessonViewHeading
                      text={t("You've Completed This Course")}
                    />
                  }
                />
              </TabPanel>
            </LegacyThemeProvider>
          </Grid>
        </Grid>
      </TabContext>
    </BackpackThemeProvider>
  );
};

interface SelectedSectionLessonViewProps {
  sectionLesson: SectionLesson;
}

const SelectedSectionLessonView = ({
  sectionLesson
}: SelectedSectionLessonViewProps) => {
  const { t } = useTranslation(
    "learnerApp\\LearnerClassroom\\ClassroomLessonsPage"
  );
  const { section } = useLearnerClassroomContext();

  // Learner can only select unlocked lessons for now and either they are completed or upcoming but nothing in between until we have a missed class view
  let heading = t("What's Next");
  if (sectionLesson.lesson?.isMissed) {
    heading = heading = t("Catch Up On Your Lessons");
  }
  if (sectionLesson?.isCompleted) {
    heading = t("Review What You’ve Learned");
  }

  if (section?.currentSectionLesson?.uid === sectionLesson.uid) {
    // Current lesson view
    return (
      <SectionLessonView
        heading={<Typography variant="h3">{heading}</Typography>}
        sectionLesson={sectionLesson}
      />
    );
  }

  return (
    <SectionLessonView
      heading={<Typography variant="h3">{heading}</Typography>}
      sectionLesson={sectionLesson}
    />
  );
};

type LessonsNavigationProps = {
  selectedLessonUid: string;
};

const LessonsNavigationForSectionLessons = React.memo(
  ({ selectedLessonUid }: LessonsNavigationProps) => {
    const { t } = useTranslation(
      "learnerApp\\LearnerClassroom\\ClassroomLessonsPage"
    );
    const currentLearner = useCurrentLearner();
    const { sectionUid, section, sectionLessons } =
      useLearnerClassroomContext();
    const { timeZone } = currentLearner || {};
    const navigate = useNavigation();
    const isMobile = useIsMobile();

    const onLessonClick = (
      _event: React.SyntheticEvent,
      sectionLessonUid: string
    ) => {
      // TODO make URL change without page reload
      navigate(LearnerRoutes.lessonsPath(sectionUid, sectionLessonUid));
    };
    const completedLessons = (sectionLessons || []).filter(
      (l: SectionLesson) => l.isCompleted
    ).length;
    // Calculate the percentage of completed lessons
    let percent = (completedLessons / (sectionLessons?.length || 1)) * 100;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Box width="100%">
            <ProgressBar
              value={percent}
              variant="determinate"
              color="inherit"
            />
          </Box>

          <Typography
            variant="overline"
            sx={(theme: $TSFixMe) => ({
              whiteSpace: "nowrap",

              // Specified as "Weight"
              fontWeight: 500,

              // Specified as "Size"
              fontSize: "12",

              // Specified as "Line height"
              lineHeight: theme.spacing(20),

              // Specified as "Letter"
              letterSpacing: "1",

              color: "neutral.dark"
            })}
          >
            {t("{{numCompletedLessons}} of {{numTotalLessons}} Lessons", {
              numCompletedLessons: completedLessons,
              numTotalLessons: sectionLessons?.length
            })}
          </Typography>
        </Box>
        <TabList
          onChange={onLessonClick}
          /* eslint-disable i18next/no-literal-string */
          orientation={isMobile ? "horizontal" : "vertical"}
          scrollButtons={isMobile ? true : "auto"}
          variant={isMobile ? "scrollable" : "standard"}
          TabIndicatorProps={{
            sx: {
              width: "3px",
              right: "initial"
            }
          }}
          /* eslint-enable i18next/no-literal-string */
        >
          {sectionLessons?.map((sectionLesson: SectionLesson) => {
            const lessonIsSelected = selectedLessonUid === sectionLesson.uid;
            const title = trimSectionLessonTitle(sectionLesson.title);

            return (
              <Tab
                onClick={e => {
                  e.stopPropagation();
                  onLessonClick(e, sectionLesson.uid);
                }}
                key={sectionLesson.uid}
                value={sectionLesson.uid}
                label={
                  <LessonNavigationLabelForSectionLessons
                    title={title}
                    meetingDate={sectionLesson?.start_time}
                    timeZone={timeZone}
                    isCancelled={
                      !section!.is_self_paced && sectionLesson.isCancelled
                    }
                    selected={lessonIsSelected}
                  />
                }
                icon={
                  isMobile
                    ? undefined
                    : LessonTabIcon({ lessonIsSelected, sectionLesson })
                }
                // eslint-disable-next-line i18next/no-literal-string
                iconPosition="end"
                disabled={false}
                sx={{
                  // Put Tab content on left, and icon on right
                  justifyContent: "space-between",
                  width: "100%",
                  padding: 16,
                  ...(lessonIsSelected
                    ? {
                        color: "primary.main",
                        backgroundColor: "primary.50"
                      }
                    : {
                        borderBottom: "1px solid",
                        borderColor: "neutral.200"
                      })
                }}
              />
            );
          })}
        </TabList>
      </Box>
    );
  }
);

interface LessonNavigationLabelForSectionLessonsProps {
  title: React.ReactNode;
  meetingDate?: Date | null;
  isCancelled?: boolean;
  timeZone?: string;
  selected?: boolean;
}
const LessonNavigationLabelForSectionLessons = ({
  title,
  meetingDate,
  timeZone,
  isCancelled = false,
  selected
}: LessonNavigationLabelForSectionLessonsProps) => {
  const { t } = useTranslation(
    "learnerApp\\LearnerClassroom\\ClassroomLessonsPage"
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        alignItems: "flex-start"
      }}
    >
      <Typography
        variant="h6"
        emphasized
        sx={{
          textAlign: "left",
          color: selected ? "primary.main" : "text.subdue"
        }}
        gutterBottom
      >
        {title}
      </Typography>
      {(meetingDate || isCancelled) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 8
          }}
        >
          <Icon
            icon={isCancelled ? faCalendarXmark : faCalendar}
            color={isCancelled ? "error" : undefined}
            size="inherit"
            fixedWidth
          />
          {isCancelled ? (
            <Typography
              variant="subtitle1"
              sx={{ textAlign: `left`, color: `error.main` }}
            >
              {t("Meeting Cancelled")}
            </Typography>
          ) : meetingDate ? (
            <Typography
              variant={`subtitle1`}
              sx={{ textAlign: `left`, color: `default` }}
            >
              {Time.fullDurationStringWithoutEndTime(meetingDate, timeZone)}
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

// TODO IMPLEMENT THIS - for now ts ignore unused function
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LessonNavigationSectionLessonPosts = ({ sectionLesson }: $TSFixMe) => {
  const { t } = useTranslation(
    "learnerApp\\LearnerClassroom\\ClassroomLessonsPage"
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 8
      }}
    >
      <Typography variant="body1">
        {t("Class Posts: {{numClassPosts}}", {
          numClassPosts: sectionLesson.classPosts?.length
        })}
      </Typography>
      <Typography variant="body1">
        {t("Assignments: {{numAssignments}}", {
          numAssignments: sectionLesson.assignmentClassPosts?.length
        })}
      </Typography>
    </Box>
  );
};

const LessonTabIcon = ({
  lessonIsSelected,
  sectionLesson
}: {
  lessonIsSelected: boolean;
  sectionLesson: SectionLesson;
}) => {
  const isLocked = sectionLesson?.isLocked;
  const isCompleted =
    sectionLesson?.lessonStatus === LearningStatementsVerb.Completed;
  const isMissed = sectionLesson?.lesson?.isMissed;

  if (isLocked) {
    return (
      <Icon
        icon={faLockRegular}
        color={lessonIsSelected ? "primary" : "inherit"}
      />
    );
  }
  if (isMissed) {
    return <Icon icon={faDiamondExclamation} color="error" />;
  }
  if (isCompleted) {
    return <Icon icon={faCheckCircle} color="success" />;
  }
  return lessonIsSelected ? (
    <Icon icon={faChevronRightRegular} color="primary" />
  ) : (
    <Icon icon={faChevronDownRegular} color="inherit" />
  );
};

const trimSectionLessonTitle = (title: string) => {
  const maxLength = 30;
  return title.length > maxLength ? `${title.slice(0, maxLength)}...` : title;
};

const IntegratedWelcomePosts = () => {
  const { posts, mentionNames, usersCanComment, handleAddComment } =
    useClassroomPostsContext();
  const welcomePosts = posts?.filter(post => post.isWelcomePost) || [];

  if (!welcomePosts.length) {
    return null;
  }

  return (
    <Box
      sx={{
        marginBottom: "2em"
      }}
    >
      {welcomePosts.map(post => (
        <LearnerClassPost
          key={post.uid}
          post={post}
          isNew={false}
          mentionNames={mentionNames}
          usersCanComment={!!usersCanComment}
          handleAddComment={handleAddComment}
        />
      ))}
    </Box>
  );
};
